import { Component, EventEmitter, OnInit } from '@angular/core';

import { AuthService } from '../../auth/auth.service';
import { NotificationService } from '../../shared/notification.service';
import { Substatus } from '../substatus';
import { SubstatusService } from '../substatus.service';

@Component({
  selector: 'app-substatus-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class SubstatusListComponent implements OnInit {
  public substatus: Substatus;

  /**
   * A list of all the substatuses currently loaded.
   *
   * @type {Substatus[]}
   */
  public substatuses: Substatus[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of substatuses.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of substatuses that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of substatuses that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a substatus has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public substatusDeleted: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {SubstatusService} substatusService
   * @param {NotificationService} notificationService
   */
  constructor(
    private substatusService: SubstatusService,
    private notificationService: NotificationService,
    protected authService: AuthService,
  ) {
    this.substatusService.loading.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });
    this.substatusService.totalPages.subscribe((totalPages) => {
      this.totalPages = totalPages;
    });
    this.substatusService.currentPage.subscribe((currentPage) => {
      this.currentPage = currentPage;
    });
    this.substatusService.totalResults.subscribe((totalResults) => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the substatus list.
   */
  public ngOnInit(): void {
    this.loadSubstatuses(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * substatus list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadSubstatuses(pageNumber);
  }

  /**
   * Loads a list of substatuses using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadSubstatuses(page?: number, limit?: number): void {
    const listSubstatuses = this.substatusService
      .listSubstatuses(page, limit)
      .subscribe((substatuses: Substatus[]) => {
        this.substatuses = substatuses;
        listSubstatuses.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate substatus ID
   * to delete the substatus.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.substatusService.deleteSubstatus(id).subscribe(
      (deleted) => {
        this.notificationService.success(
          `Substatus Deleted`,
          `Substatus with ID ${id} was deleted.`,
          5,
        );
        this.substatusDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      (err) => {
        this.notificationService.error(
          `Substatus Not Deleted`,
          err.error.message,
          10,
        );
        this.substatusDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
